import * as XLSX from "xlsx";
import moment from "moment";
import { capitalizeWords } from "functions";

/**
 * Loads the jsPDF constructor across various module bundling environments (Webpack/ESM/UMD).
 */
const loadJsPDF = async () => {
  try {
    const m = await import("jspdf");
    const C = m.jsPDF || m.default?.jsPDF || m.default || m;
    if (typeof C === "function") return C;
  } catch (e) {
    console.warn("jsPDF dynamic import:", e);
  }
  if (typeof window !== "undefined") {
    const winPdf = window.jspdf?.jsPDF || window.jsPDF;
    if (typeof winPdf === "function") return winPdf;
  }
  return null;
};

/**
 * Common table drawing function for jsPDF in landscape A4 orientation
 */
const drawPDFReport = (doc, {
  title,
  subtitle,
  brandColor = [26, 54, 93],     // Primary header color
  accentColor = [217, 119, 6],    // Accent bar
  columns,
  rows,
  emptyMessage = "No records found matching criteria",
}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;
  const contentWidth = pageWidth - margin * 2; // ~273mm

  let currentY = 12;

  const drawHeaderBanner = (isFirstPage = true) => {
    if (isFirstPage) {
      // Main Top Banner
      doc.setFillColor(...brandColor);
      doc.roundedRect(margin, currentY, contentWidth, 21, 2, 2, "F");

      // Accent gold bottom line
      doc.setFillColor(...accentColor);
      doc.rect(margin, currentY + 20, contentWidth, 1.2, "F");

      // Left: Company Branding
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("DAHIYA INSURANCE", margin + 6, currentY + 8.5);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(226, 232, 240);
      doc.text("Life Insurance Corporation of India (LIC) Agency & Client Management", margin + 6, currentY + 14);

      // Right: Report Title & Subtitle
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(title.toUpperCase(), margin + contentWidth - 6, currentY + 8.5, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(253, 230, 138); // light gold
      doc.text(subtitle, margin + contentWidth - 6, currentY + 14, { align: "right" });

      currentY += 26;
    } else {
      // Mini Header for Subsequent Pages
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, currentY, contentWidth, 7, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...brandColor);
      doc.text("DAHIYA INSURANCE  —  " + title.toUpperCase(), margin + 4, currentY + 4.8);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(subtitle, margin + contentWidth - 4, currentY + 4.8, { align: "right" });

      currentY += 10;
    }

    // Table Header Row
    doc.setFillColor(...brandColor);
    doc.rect(margin, currentY, contentWidth, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);

    let x = margin;
    columns.forEach((col) => {
      const align = col.align || "left";
      const textX = align === "right" ? x + col.width - 2 : x + 2;
      doc.text(col.header, textX, currentY + 5.5, { align });
      x += col.width;
    });

    currentY += 8;
  };

  drawHeaderBanner(true);

  if (rows.length === 0) {
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, currentY, contentWidth, 16, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(emptyMessage, margin + contentWidth / 2, currentY + 10, { align: "center" });
    currentY += 16;
  } else {
    // Draw Data Rows
    rows.forEach((row, rowIdx) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);

      const cellLines = columns.map((col) => {
        const val = row[col.key] != null ? String(row[col.key]) : "—";
        return doc.splitTextToSize(val, col.width - 3);
      });

      const maxLines = Math.max(...cellLines.map((lines) => lines.length), 1);
      const lineHeight = 3.6;
      const rowHeight = Math.max(maxLines * lineHeight + 3, 7.5);

      // Page break check
      if (currentY + rowHeight > pageHeight - 16) {
        doc.addPage();
        currentY = 12;
        drawHeaderBanner(false);
      }

      // Zebra striping background
      const isEven = rowIdx % 2 === 0;
      doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 252);
      doc.rect(margin, currentY, contentWidth, rowHeight, "F");

      // Row separator line
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.line(margin, currentY + rowHeight, margin + contentWidth, currentY + rowHeight);

      // Render cell text
      doc.setTextColor(30, 41, 59);
      let x = margin;
      columns.forEach((col, cIdx) => {
        const lines = cellLines[cIdx];
        const align = col.align || "left";
        const textX = align === "right" ? x + col.width - 2 : x + 2;

        let lineY = currentY + 4.2;
        lines.forEach((line) => {
          doc.text(line, textX, lineY, { align });
          lineY += lineHeight;
        });
        x += col.width;
      });

      currentY += rowHeight;
    });
  }

  // Add Page Numbers & Footers
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);

    // Footer divider line
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 10, margin + contentWidth, pageHeight - 10);

    doc.text(
      `Confidential — Dahiya Insurance CRM Pro | Generated: ${moment().format("DD-MM-YYYY hh:mm A")}`,
      margin,
      pageHeight - 6
    );
    doc.text(`Page ${i} of ${totalPages}`, margin + contentWidth, pageHeight - 6, { align: "right" });
  }
};

// ─── FOLLOWUPS EXPORT UTILITIES ──────────────────────────────────────────────

/**
 * Exports followups list to Excel (.xlsx)
 */
export function exportFollowupsToExcel(followupsList = [], filterTitle = "Followups") {
  if (!Array.isArray(followupsList) || followupsList.length === 0) {
    alert("No followups available to download.");
    return;
  }

  const exportData = followupsList.map((f, idx) => ({
    "S.No": idx + 1,
    "Client / Prospect Name": capitalizeWords(f.Name || "—"),
    "Family Member": capitalizeWords(f.FamilyMember || f.FamilyMemberName || "—"),
    "Reference / ID": f.Reference || f.ID || "—",
    "Mobile No": f.Mobile || f.MobileNo || "—",
    "WhatsApp No": f.WhatsAppNo || "—",
    "Appointment Date & Time": f.NextAppointment || "—",
    "Status": (f._category || f.AppointmentStatus || "—").toUpperCase(),
    "Next Place": f.NextPlace || f.Place || "—",
    "Discussion / Notes": f.Discussed || f.Discussion || f.Subject || "—",
    "Added By": f.AddedBy || "—",
  }));

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exportData);

  ws["!cols"] = [
    { wch: 6 },   // S.No
    { wch: 28 },  // Client Name
    { wch: 24 },  // Family Member
    { wch: 18 },  // Reference/ID
    { wch: 15 },  // Mobile
    { wch: 15 },  // WhatsApp
    { wch: 22 },  // Appointment Date & Time
    { wch: 14 },  // Status
    { wch: 20 },  // Next Place
    { wch: 38 },  // Discussion / Notes
    { wch: 18 },  // Added By
  ];

  XLSX.utils.book_append_sheet(wb, ws, "Followups");
  const fileName = `Followups_${filterTitle.replace(/[^a-zA-Z0-9]/g, "_")}_${moment().format("DDMMYYYY_HHmm")}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

/**
 * Exports followups list to PDF (Landscape A4)
 */
export async function exportFollowupsToPDF(followupsList = [], filterTitle = "Followups Schedule") {
  if (!Array.isArray(followupsList) || followupsList.length === 0) {
    alert("No followups available to download.");
    return;
  }

  const JsPdfClass = await loadJsPDF();
  if (!JsPdfClass) {
    alert("Could not initialize PDF generator. Please try again.");
    return;
  }

  const doc = new JsPdfClass({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const columns = [
    { header: "#", key: "sno", width: 10, align: "center" },
    { header: "Client / Prospect Name", key: "name", width: 48 },
    { header: "Reference / ID", key: "ref", width: 28 },
    { header: "Mobile No", key: "mobile", width: 26 },
    { header: "Appointment Time", key: "time", width: 38 },
    { header: "Status", key: "status", width: 22, align: "center" },
    { header: "Next Place", key: "place", width: 35 },
    { header: "Discussion / Notes", key: "notes", width: 66 },
  ];

  const rows = followupsList.map((f, idx) => ({
    sno: idx + 1,
    name: capitalizeWords(f.Name || "—"),
    ref: f.Reference || f.ID || "—",
    mobile: f.Mobile || f.MobileNo || "—",
    time: f.NextAppointment || "Not set",
    status: (f._category || f.AppointmentStatus || "—").toUpperCase(),
    place: f.NextPlace || f.Place || "—",
    notes: f.Discussed || f.Discussion || f.Subject || "—",
  }));

  const subtitle = `Filter: ${filterTitle}  |  Total: ${followupsList.length} Records  |  Date: ${moment().format("DD MMM YYYY")}`;

  drawPDFReport(doc, {
    title: "Pending Followups Schedule",
    subtitle,
    brandColor: [26, 54, 93],    // Navy
    accentColor: [217, 119, 6],   // Gold
    columns,
    rows,
  });

  const fileName = `Followups_${filterTitle.replace(/[^a-zA-Z0-9]/g, "_")}_${moment().format("DDMMYYYY_HHmm")}.pdf`;
  doc.save(fileName);
}

// ─── CELEBRATIONS EXPORT UTILITIES ──────────────────────────────────────────

/**
 * Exports celebrations list (Birthdays or Anniversaries) to Excel (.xlsx)
 */
export function exportCelebrationsToExcel(celebrantsList = [], category = "Birthday", dayFilter = "Both") {
  if (!Array.isArray(celebrantsList) || celebrantsList.length === 0) {
    alert(`No ${category.toLowerCase()} celebrations available to download.`);
    return;
  }

  const isBirthday = category === "Birthday";

  const exportData = celebrantsList.map((c, idx) => ({
    "S.No": idx + 1,
    "Family Member Name": capitalizeWords(c.FamilyMember || c.FamilyMemberName || "—"),
    "Main Member / Client": capitalizeWords(c.MainMemberName || "—"),
    "Reference / ID": c.ID || "—",
    "Mobile No": c.Mobile || "—",
    "WhatsApp No": c.WhatsAppNo || c.Mobile || "—",
    "Village": c.Village || "—",
    "Tehsil": c.Tehsil || "—",
    "District": c.District || "—",
    "State": c.State || "—",
    "Celebration Date (Current Year)": c.Date ? moment(c.Date).format("DD MMM YYYY") : "—",
    "Actual Birth/Marriage Date (Original Date)": c.OriginalDate ? moment(c.OriginalDate).format("DD MMM YYYY") : "—",
    "Years Completed": c.YearsCompleted != null ? c.YearsCompleted : "—",
    "Day Category": c.DateCategory || "—",
    "Type": c.Type || "Prospect",
  }));

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exportData);

  ws["!cols"] = [
    { wch: 6 },   // S.No
    { wch: 28 },  // Member Name
    { wch: 26 },  // Main Member
    { wch: 18 },  // ID
    { wch: 15 },  // Mobile
    { wch: 15 },  // WhatsApp
    { wch: 20 },  // Village
    { wch: 16 },  // Tehsil
    { wch: 16 },  // District
    { wch: 16 },  // State
    { wch: 24 },  // Celebration Date
    { wch: 32 },  // Original Date
    { wch: 16 },  // Years
    { wch: 14 },  // Category
    { wch: 12 },  // Type
  ];

  XLSX.utils.book_append_sheet(wb, ws, isBirthday ? "Birthdays" : "Anniversaries");
  const fileName = `${category}_Celebrations_${dayFilter}_${moment().format("DDMMYYYY_HHmm")}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

/**
 * Exports celebrations list (Birthdays or Anniversaries) to PDF (Landscape A4)
 */
export async function exportCelebrationsToPDF(celebrantsList = [], category = "Birthday", dayFilter = "Both") {
  if (!Array.isArray(celebrantsList) || celebrantsList.length === 0) {
    alert(`No ${category.toLowerCase()} celebrations available to download.`);
    return;
  }

  const JsPdfClass = await loadJsPDF();
  if (!JsPdfClass) {
    alert("Could not initialize PDF generator. Please try again.");
    return;
  }

  const doc = new JsPdfClass({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const isBirthday = category === "Birthday";

  // Brand colors: Amber for Birthdays, Rose Pink for Anniversaries
  const brandColor = isBirthday ? [180, 83, 9] : [157, 23, 77];     // #B45309 or #9D174D
  const accentColor = isBirthday ? [245, 158, 11] : [236, 72, 153]; // #F59E0B or #EC4899

  const columns = [
    { header: "#", key: "sno", width: 8, align: "center" },
    { header: isBirthday ? "Celebrant / Member Name" : "Couple / Member Name", key: "name", width: 42 },
    { header: "Main Member / Client", key: "mainMember", width: 36 },
    { header: "Reference / ID", key: "ref", width: 24 },
    { header: "Mobile No", key: "mobile", width: 24 },
    { header: "Village / Location", key: "location", width: 45 },
    { header: "Celebration Date", key: "date", width: 24 },
    { header: isBirthday ? "Birth Date (DOB)" : "Marriage Date (DOM)", key: "origDate", width: 28 },
    { header: isBirthday ? "Age / Yrs" : "Anniv Yrs", key: "years", width: 22, align: "center" },
    { header: "Day", key: "category", width: 20, align: "center" },
  ];

  const rows = celebrantsList.map((c, idx) => ({
    sno: idx + 1,
    name: capitalizeWords(c.FamilyMember || c.FamilyMemberName || "—"),
    mainMember: capitalizeWords(c.MainMemberName || "—"),
    ref: c.ID || "—",
    mobile: c.Mobile || "—",
    location: [c.Village, c.Tehsil, c.District, c.State].filter(Boolean).join(", ") || "—",
    date: c.Date ? moment(c.Date).format("DD MMM YYYY") : "—",
    origDate: c.OriginalDate ? moment(c.OriginalDate).format("DD MMM YYYY") : "—",
    years: c.YearsCompleted != null ? `${c.YearsCompleted} Yrs` : "—",
    category: c.DateCategory || "—",
  }));

  const subtitle = `Type: ${isBirthday ? "Birthdays" : "Anniversaries"}  |  Filter / Period: ${dayFilter}  |  Total: ${celebrantsList.length} Records  |  Generated: ${moment().format("DD MMM YYYY")}`;

  drawPDFReport(doc, {
    title: `${category} Celebrations List`,
    subtitle,
    brandColor,
    accentColor,
    columns,
    rows,
  });

  const fileName = `${category}_Celebrations_${dayFilter.replace(/[^a-zA-Z0-9]/g, "_")}_${moment().format("DDMMYYYY_HHmm")}.pdf`;
  doc.save(fileName);
}

// ─── CALENDAR FULL SCHEDULE EXPORT UTILITIES ────────────────────────────────

/**
 * Exports complete calendar schedule (Followups, Birthdays, Anniversaries) to a multi-sheet Excel (.xlsx) workbook
 */
export function exportCalendarScheduleToExcel({
  followups = [],
  birthdays = [],
  anniversaries = [],
  monthName = moment().format("MMMM"),
  year = moment().format("YYYY"),
} = {}) {
  const wb = XLSX.utils.book_new();

  // 1. All Combined Events Sheet
  const combinedEvents = [
    ...birthdays.map((b) => ({
      Type: "Birthday",
      Date: b.Date ? moment(b.Date).format("YYYY-MM-DD") : "",
      FormattedDate: b.Date ? moment(b.Date).format("DD MMM YYYY") : "—",
      Name: capitalizeWords(b.FamilyMember || b.FamilyMemberName || "—"),
      MainMember: capitalizeWords(b.MainMemberName || "—"),
      ID: b.ID || "—",
      Mobile: b.Mobile || "—",
      WhatsApp: b.WhatsAppNo || b.Mobile || "—",
      Village: b.Village || "—",
      Tehsil: b.Tehsil || "—",
      District: b.District || "—",
      State: b.State || "—",
      CelebrationDate: b.Date ? moment(b.Date).format("DD MMM YYYY") : "—",
      OriginalDate: b.OriginalDate ? moment(b.OriginalDate).format("DD MMM YYYY") : "—",
      Details: `Birthday | ${b.YearsCompleted != null ? `${b.YearsCompleted} Yrs` : "—"} | Category: ${b.DateCategory || "—"}`,
    })),
    ...anniversaries.map((a) => ({
      Type: "Anniversary",
      Date: a.Date ? moment(a.Date).format("YYYY-MM-DD") : "",
      FormattedDate: a.Date ? moment(a.Date).format("DD MMM YYYY") : "—",
      Name: capitalizeWords(a.FamilyMember || a.FamilyMemberName || "—"),
      MainMember: capitalizeWords(a.MainMemberName || "—"),
      ID: a.ID || "—",
      Mobile: a.Mobile || "—",
      WhatsApp: a.WhatsAppNo || a.Mobile || "—",
      Village: a.Village || "—",
      Tehsil: a.Tehsil || "—",
      District: a.District || "—",
      State: a.State || "—",
      CelebrationDate: a.Date ? moment(a.Date).format("DD MMM YYYY") : "—",
      OriginalDate: a.OriginalDate ? moment(a.OriginalDate).format("DD MMM YYYY") : "—",
      Details: `Anniversary | ${a.YearsCompleted != null ? `${a.YearsCompleted}th Yr` : "—"} | Category: ${a.DateCategory || "—"}`,
    })),
    ...followups.map((f) => ({
      Type: "Followup",
      Date: f.NextAppointment ? moment(f.NextAppointment, "DD/MM/YYYY hh:mm A").format("YYYY-MM-DD") : "",
      FormattedDate: f.NextAppointment || "—",
      Name: capitalizeWords(f.Name || "—"),
      MainMember: capitalizeWords(f.FamilyMember || "—"),
      ID: f.Reference || f.ID || "—",
      Mobile: f.Mobile || f.MobileNo || "—",
      WhatsApp: f.WhatsAppNo || "—",
      Village: f.Village || "—",
      Tehsil: f.Tehsil || "—",
      District: f.District || "—",
      State: f.State || "—",
      CelebrationDate: f.NextAppointment || "—",
      OriginalDate: "—",
      Details: `Followup | Status: ${f.AppointmentStatus || "—"} | Place: ${f.NextPlace || "—"} | Discussed: ${f.Discussed || f.Discussion || "—"}`,
    })),
  ];

  combinedEvents.sort((a, b) => (a.Date > b.Date ? 1 : a.Date < b.Date ? -1 : 0));

  const allEventsExportData = combinedEvents.map((e, idx) => ({
    "S.No": idx + 1,
    "Event Type": e.Type,
    "Date & Time": e.FormattedDate,
    "Name / Member": e.Name,
    "Main Member / Client": e.MainMember,
    "Reference / ID": e.ID,
    "Mobile No": e.Mobile,
    "WhatsApp No": e.WhatsApp,
    "Village": e.Village,
    "Tehsil": e.Tehsil,
    "District": e.District,
    "State": e.State,
    "Original Date (DOB/DOM)": e.OriginalDate,
    "Details / Notes / Status": e.Details,
  }));

  const wsAll = XLSX.utils.json_to_sheet(allEventsExportData);
  wsAll["!cols"] = [
    { wch: 6 },
    { wch: 14 },
    { wch: 20 },
    { wch: 26 },
    { wch: 24 },
    { wch: 16 },
    { wch: 15 },
    { wch: 15 },
    { wch: 18 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 24 },
    { wch: 45 },
  ];
  XLSX.utils.book_append_sheet(wb, wsAll, "All Schedule");

  // 2. Birthdays Sheet
  if (birthdays.length > 0) {
    const bdayData = birthdays.map((b, idx) => ({
      "S.No": idx + 1,
      "Family Member Name": capitalizeWords(b.FamilyMember || b.FamilyMemberName || "—"),
      "Main Member / Client": capitalizeWords(b.MainMemberName || "—"),
      "Reference / ID": b.ID || "—",
      "Mobile No": b.Mobile || "—",
      "WhatsApp No": b.WhatsAppNo || b.Mobile || "—",
      "Village": b.Village || "—",
      "Tehsil": b.Tehsil || "—",
      "District": b.District || "—",
      "State": b.State || "—",
      "Celebration Date": b.Date ? moment(b.Date).format("DD MMM YYYY") : "—",
      "Original Birth Date": b.OriginalDate ? moment(b.OriginalDate).format("DD MMM YYYY") : "—",
      "Years / Age": b.YearsCompleted != null ? b.YearsCompleted : "—",
      "Day Category": b.DateCategory || "—",
      "Type": b.Type || "Prospect",
    }));
    const wsBday = XLSX.utils.json_to_sheet(bdayData);
    XLSX.utils.book_append_sheet(wb, wsBday, `Birthdays (${monthName})`);
  }

  // 3. Anniversaries Sheet
  if (anniversaries.length > 0) {
    const annivData = anniversaries.map((a, idx) => ({
      "S.No": idx + 1,
      "Couple / Member Name": capitalizeWords(a.FamilyMember || a.FamilyMemberName || "—"),
      "Main Member / Client": capitalizeWords(a.MainMemberName || "—"),
      "Reference / ID": a.ID || "—",
      "Mobile No": a.Mobile || "—",
      "WhatsApp No": a.WhatsAppNo || a.Mobile || "—",
      "Village": a.Village || "—",
      "Tehsil": a.Tehsil || "—",
      "District": a.District || "—",
      "State": a.State || "—",
      "Celebration Date": a.Date ? moment(a.Date).format("DD MMM YYYY") : "—",
      "Original Marriage Date": a.OriginalDate ? moment(a.OriginalDate).format("DD MMM YYYY") : "—",
      "Anniversary Years": a.YearsCompleted != null ? a.YearsCompleted : "—",
      "Day Category": a.DateCategory || "—",
      "Type": a.Type || "Prospect",
    }));
    const wsAnniv = XLSX.utils.json_to_sheet(annivData);
    XLSX.utils.book_append_sheet(wb, wsAnniv, `Anniversaries (${monthName})`);
  }

  // 4. Followups Sheet
  if (followups.length > 0) {
    const followupData = followups.map((f, idx) => ({
      "S.No": idx + 1,
      "Client / Prospect Name": capitalizeWords(f.Name || "—"),
      "Family Member": capitalizeWords(f.FamilyMember || "—"),
      "Reference / ID": f.Reference || f.ID || "—",
      "Mobile No": f.Mobile || f.MobileNo || "—",
      "WhatsApp No": f.WhatsAppNo || "—",
      "Appointment Date & Time": f.NextAppointment || "—",
      "Status": (f.AppointmentStatus || "—").toUpperCase(),
      "Next Place": f.NextPlace || f.Place || "—",
      "Discussion / Notes": f.Discussed || f.Discussion || f.Subject || "—",
      "Added By": f.AddedBy || "—",
    }));
    const wsFollowup = XLSX.utils.json_to_sheet(followupData);
    XLSX.utils.book_append_sheet(wb, wsFollowup, "Pending Followups");
  }

  const fileName = `Calendar_Schedule_${monthName}_${year}_${moment().format("DDMMYYYY_HHmm")}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

/**
 * Exports combined calendar schedule to PDF (Landscape A4)
 */
export async function exportCalendarScheduleToPDF({
  followups = [],
  birthdays = [],
  anniversaries = [],
  monthName = moment().format("MMMM"),
  year = moment().format("YYYY"),
} = {}) {
  const JsPdfClass = await loadJsPDF();
  if (!JsPdfClass) {
    alert("Could not initialize PDF generator. Please try again.");
    return;
  }

  const doc = new JsPdfClass({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const brandColor = [26, 54, 93];     // #1A365D Primary Deep Blue
  const accentColor = [217, 119, 6];    // #D97706 Warm Amber

  const combinedEvents = [
    ...birthdays.map((b) => ({
      type: "Birthday",
      dateVal: b.Date ? moment(b.Date).format("YYYY-MM-DD") : "",
      date: b.Date ? moment(b.Date).format("DD MMM YYYY") : "—",
      name: capitalizeWords(b.FamilyMember || b.FamilyMemberName || "—"),
      ref: b.ID || "—",
      mobile: b.Mobile || "—",
      location: [b.Village, b.Tehsil, b.District, b.State].filter(Boolean).join(", ") || "—",
      details: `Birthday | ${b.YearsCompleted != null ? `${b.YearsCompleted} Yrs` : ""}${b.OriginalDate ? ` (DOB: ${moment(b.OriginalDate).format("DD/MM/YYYY")})` : ""}`,
    })),
    ...anniversaries.map((a) => ({
      type: "Anniversary",
      dateVal: a.Date ? moment(a.Date).format("YYYY-MM-DD") : "",
      date: a.Date ? moment(a.Date).format("DD MMM YYYY") : "—",
      name: capitalizeWords(a.FamilyMember || a.FamilyMemberName || "—"),
      ref: a.ID || "—",
      mobile: a.Mobile || "—",
      location: [a.Village, a.Tehsil, a.District, a.State].filter(Boolean).join(", ") || "—",
      details: `Anniversary | ${a.YearsCompleted != null ? `${a.YearsCompleted}th Yr` : ""}${a.OriginalDate ? ` (DOM: ${moment(a.OriginalDate).format("DD/MM/YYYY")})` : ""}`,
    })),
    ...followups.map((f) => ({
      type: "Followup",
      dateVal: f.NextAppointment ? moment(f.NextAppointment, "DD/MM/YYYY hh:mm A").format("YYYY-MM-DD") : "",
      date: f.NextAppointment ? f.NextAppointment.split(" ")[0] : "—",
      name: capitalizeWords(f.Name || "—"),
      ref: f.Reference || f.ID || "—",
      mobile: f.Mobile || f.MobileNo || "—",
      location: [f.Village, f.Tehsil, f.NextPlace || f.Place].filter(Boolean).join(", ") || "—",
      details: `Followup [${f.AppointmentStatus || "Future"}] | ${f.Discussed || f.Discussion || f.NextPlace || "Followup Scheduled"}`,
    })),
  ];

  combinedEvents.sort((a, b) => (a.dateVal > b.dateVal ? 1 : a.dateVal < b.dateVal ? -1 : 0));

  const columns = [
    { header: "#", key: "sno", width: 8, align: "center" },
    { header: "Type", key: "type", width: 25 },
    { header: "Date", key: "date", width: 24 },
    { header: "Name / Prospect", key: "name", width: 44 },
    { header: "Mobile No", key: "mobile", width: 24 },
    { header: "Location (Village/City)", key: "location", width: 44 },
    { header: "Details / Schedule Notes", key: "details", width: 104 },
  ];

  const rows = combinedEvents.map((e, idx) => ({
    sno: idx + 1,
    type: e.type,
    date: e.date,
    name: e.name,
    mobile: e.mobile,
    location: e.location,
    details: e.details,
  }));

  const subtitle = `Month: ${monthName} ${year}  |  Total: ${combinedEvents.length} Events (Birthdays: ${birthdays.length}, Anniversaries: ${anniversaries.length}, Followups: ${followups.length})  |  Generated: ${moment().format("DD MMM YYYY")}`;

  drawPDFReport(doc, {
    title: `Calendar Schedule — ${monthName} ${year}`,
    subtitle,
    brandColor,
    accentColor,
    columns,
    rows,
  });

  const fileName = `Calendar_Schedule_${monthName}_${year}_${moment().format("DDMMYYYY_HHmm")}.pdf`;
  doc.save(fileName);
}

